/**
 * Number grouping. Organizations pick a default (Pakistani `12,50,000` vs
 * international `1,250,000`, docs/9-ui.md) and a user may override it personally
 * (HS2-ZHZ39R). Kept as a pure module so the grouping logic is unit-testable.
 * Amounts themselves format through Web Awesome with the resolved locale
 * (`currencyLocale()`, HS2-76RBPE).
 */
export type Grouping = 'pakistani' | 'international';

/** Resolve the effective grouping from the user's preference and the org default.
 *  A personal value of 'org' (or absent/unknown) falls back to the org default. */
export function effectiveGrouping(personal: unknown, orgDefault: unknown): Grouping {
  const org: Grouping = orgDefault === 'international' ? 'international' : 'pakistani';
  if (personal === 'pakistani' || personal === 'international') return personal;
  return org;
}

/** Format a date-only value (`YYYY-MM-DD`) for display, e.g. `Nov 20, 2026`
 *  (locale-dependent). Parsed at local midnight so the rendered day never shifts
 *  by timezone. Returns the input unchanged if it is not a valid date (HS2-8TG0P1). */
export function formatDateOnly(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? isoDate
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A human-readable file size (e.g. `840 KB`, `2.4 MB`) from a byte count. Returns
 *  '' for a missing/invalid size so the UI can omit it (HS2-YP7T7Q). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${String(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : String(Math.round(v))} ${units[i] ?? 'KB'}`;
}

/** A note's timestamp, e.g. "Oct 14, 2:05 PM" (HS2-E33XQD). */
export function formatNoteTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
